import { useCallback, useState } from "react";
import type { HomeWidgetId } from "@heraion/shared";

// Home's quick-entry drafts (bodyweight, glucose), lifted ABOVE HomeView
// (MX-8b, following MX-8): HomeView remounts its whole subtree crossing the
// wide/narrow breakpoint (two separate JSX branches, HomeView.tsx's wide ?
// two-column : single-scroll), so a draft held inside QuickWeightCard /
// QuickGlucoseCard itself is lost on resize. The fix is not to touch layout
// or the cards' own uncontrolled default; it is to hold the draft one level
// up (in each PlanScreen, mobile and web-rnw) and hand it down through the
// cards' new optional value/onChangeText, so the remount re-reads the same
// state instead of re-initialising it.
//
// Keyed by WIDGET ID, never position: HR-11 lets the athlete reorder Home's
// widgets, and an id-keyed draft does not care which slot a card renders in.
// The ids are the Home widget catalog's (@heraion/shared homeLayout.ts),
// narrowed to the two cards that hold a draft.
export type HomeCardWidgetId = Extract<
  HomeWidgetId,
  "quickWeight" | "quickGlucose"
>;

export function useHomeCardDrafts(): {
  draft: (id: HomeCardWidgetId) => string;
  setDraft: (id: HomeCardWidgetId, value: string) => void;
} {
  const [drafts, setDrafts] = useState<
    Partial<Record<HomeCardWidgetId, string>>
  >({});

  const draft = useCallback(
    (id: HomeCardWidgetId): string => drafts[id] ?? "",
    [drafts],
  );
  const setDraft = useCallback((id: HomeCardWidgetId, value: string) => {
    setDrafts((prev) =>
      prev[id] === value ? prev : { ...prev, [id]: value },
    );
  }, []);

  return { draft, setDraft };
}
